import {
  type InfiniteData,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import api from '@/axios';
import { getLotSizingLinesBySupplierInfiniteQueryKey } from '@/features/lot-sizing/hooks/use-lot-sizing-lines-by-supplier';
import { GET_LOT_SIZING_KEY } from '@/keys';
import type { LotSizingLine } from '@/types/core/lot-sizing/lot-sizing-line';
import type { Paginated } from '@/types/core/paginated';

export type UpdateLotSizingLineParams = {
  lotSizingId: string;
  supplierId: string;
  lineId: string;
  quantity?: number;
  checked?: boolean;
};

export async function getLotSizingLine(
  lineId: string,
): Promise<LotSizingLine | null> {
  const { data, status } = await api.get<LotSizingLine>(
    `/lot-sizing-lines/${lineId}`,
    { validateStatus: (s) => s === 200 || s === 404 },
  );
  return status === 200 ? data : null;
}

function updateLineInCache(
  prev: InfiniteData<Paginated<LotSizingLine>> | undefined,
  lineId: string,
  updater: (line: LotSizingLine) => LotSizingLine,
): InfiniteData<Paginated<LotSizingLine>> | undefined {
  if (!prev) {
    return prev;
  }
  return {
    ...prev,
    pages: prev.pages.map((page) => ({
      ...page,
      data: page.data.map((l) => (String(l.id) === lineId ? updater(l) : l)),
    })),
  };
}

const updateLotSizingLine = async ({
  lineId,
  quantity,
  checked,
}: UpdateLotSizingLineParams): Promise<void> => {
  await api.patch(`/lot-sizing-lines/${lineId}`, {
    ...(quantity !== undefined && { quantity }),
    ...(checked !== undefined && { checked }),
  });
};

export function useUpdateLotSizingLine(): UseMutationResult<
  void,
  Error,
  UpdateLotSizingLineParams
> {
  const queryClient = useQueryClient();

  const getQueryKey = (lotSizingId: string, supplierId: string) =>
    getLotSizingLinesBySupplierInfiniteQueryKey(lotSizingId, supplierId);

  return useMutation({
    mutationFn: updateLotSizingLine,
    onMutate: async ({
      lotSizingId,
      supplierId,
      lineId,
      quantity,
      checked,
    }) => {
      const queryKey = getQueryKey(lotSizingId, supplierId);
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<InfiniteData<Paginated<LotSizingLine>>>(
          queryKey,
        );
      queryClient.setQueryData<InfiniteData<Paginated<LotSizingLine>>>(
        queryKey,
        (prev) =>
          updateLineInCache(prev, lineId, (l) => ({
            ...l,
            ...(quantity !== undefined && { quantity }),
            ...(checked !== undefined && { checked }),
          })),
      );
      return { previous, queryKey };
    },
    onError: (_err, _variables, context) => {
      if (
        context !== undefined &&
        context.previous !== null &&
        context.queryKey !== null
      ) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
    },
    onSettled: async (_data, _error, { lotSizingId, supplierId, lineId }) => {
      const queryKey = getQueryKey(lotSizingId, supplierId);
      const lineFromApi = await getLotSizingLine(lineId);
      if (lineFromApi === null) {
        await queryClient.invalidateQueries({ queryKey });
        return;
      }
      await queryClient.refetchQueries({
        queryKey: [...GET_LOT_SIZING_KEY, lotSizingId],
      });
      queryClient.setQueryData<InfiniteData<Paginated<LotSizingLine>>>(
        queryKey,
        (prev) => updateLineInCache(prev, lineId, () => lineFromApi),
      );
    },
  });
}
